import React from 'react';
import { Alert, Box, Card, Icon, List, Text } from '@nimbus-ds/components';
import { FileAltIcon, FileIcon, TruckIcon } from '@nimbus-ds/icons';
import { FormattedMultilineText, TextWithLink } from 'commons/components';
import { useCountry } from 'commons/hooks';
import { ChargebackReason } from 'commons/types';
import { useTranslationWithPrefix } from 'commons/utils';
import { config } from 'config';
import ChargebackDocumentUploader from '../ChargebackDocumentUploader';
import UploadCardSkeleton from './UploadCardSkeleton';

interface UploadCardProps {
  error: string;
  orderId: string;
  chargebackReason: ChargebackReason;
  onSuccess: (key: string, duration: number) => void;
  onFileError: (error: string) => void;
  onUploadError: () => void;
  onDelete: () => void;
  disabled?: boolean;
}

function UploadCard({
  error,
  orderId,
  chargebackReason,
  onSuccess,
  onFileError,
  onUploadError,
  onDelete,
  disabled,
}: UploadCardProps) {
  const { t } = useTranslationWithPrefix('chargebackUpload.uploadCard');
  const { isMexico, isArgentina } = useCountry();

  const maxDocumentSize = isMexico
    ? config.documents.maxChargebackSizeMx
    : config.documents.maxChargebackSizeDefault;

  const isDisputeInArg =
    chargebackReason === ChargebackReason.Dispute && isArgentina;

  return (
    <Card data-testid="chargeback-upload-upload-card">
      <Card.Header title={t(`title.${chargebackReason}`)} />
      <Card.Body>
        <Box display="flex" flexDirection="column" gap="4">
          <Box
            display="flex"
            flexDirection="row"
            gap="2"
            alignItems="flex-start"
          >
            <Icon source={<TruckIcon />} />
            {isDisputeInArg ? (
              <Box display="flex" flexDirection="column" gap="2">
                <Text>{t('description.dispute.intro')}</Text>
                <List>
                  <List.Item>
                    {t('description.dispute.shippingEvidence')}
                  </List.Item>
                  <List.Item>
                    {t('description.dispute.clientEvidence')}
                  </List.Item>
                </List>
                <TextWithLink
                  t={t}
                  i18nKey="description.dispute.tip"
                  href={t('description.dispute.tipHref')}
                />
              </Box>
            ) : (
              <TextWithLink
                t={t}
                i18nKey="description.fraud"
                href={t('shippingHref')}
              />
            )}
          </Box>
          <Box display="flex" flexDirection="row" gap="2">
            <Icon source={<FileAltIcon />} />
            <FormattedMultilineText formattedFontWeight="bold">
              {t('fileFormat')}
            </FormattedMultilineText>
          </Box>
          <Box display="flex" flexDirection="row" gap="2">
            <Icon source={<FileIcon />} />
            <FormattedMultilineText formattedFontWeight="bold">
              {t('fileSize')}
            </FormattedMultilineText>
          </Box>
          <Alert show={Boolean(error)} appearance="danger">
            {error}
          </Alert>
          <ChargebackDocumentUploader
            documentName="chargeback"
            ariaLabel="chargeback"
            helperText={
              isDisputeInArg ? t('helperTextForDispute') : t('helperText')
            }
            accept="application/pdf, image/jpeg, image/png"
            onSuccess={onSuccess}
            onFileError={onFileError}
            onUploadError={onUploadError}
            maxDocumentSize={maxDocumentSize}
            onDelete={onDelete}
            orderId={orderId}
            disabled={disabled}
          />
        </Box>
      </Card.Body>
    </Card>
  );
}

UploadCard.Skeleton = UploadCardSkeleton;
export default UploadCard;
